import React from 'react';
import {
  ColumnIdentifier,
  Table,
  TableBody,
  TableCell,
  TableCellDescription,
  TableCellPageLink,
  TableHead,
  TableModifier,
  TableRow,
} from '../../../components/organisms/Table';
import { useSonTranslation } from '../../../translation/useSonTranslation';
import { AToZContainer, AToZToNavButton } from '../../../_lib/components/AToZContainer';
import { Facets } from '../../../types/Facets';
import { isNullOrUndefined } from '../../../lib/typeGuards';
import { ContentAttachmentList } from '../../../components/molecules/ContentAttachmentList/ContentAttachmentList';
import { getDisplayedTextByConfig } from '../PageListDetails.utils';
import { DisplayOptions, PageListDetailsSearchResults, PageListItem } from '../PageListDetails.types';
import { SearchFormBase, SearchFormModifier } from '../../Search/partials/SearchFormBase';

type PageListAToZProps = {
  pageListAToZ?: Record<string, PageListItem[]>;
  displayOptions: DisplayOptions;
  facets?: Facets;
  resetAllFiltersUrl?: {
    url: string;
  };
  searchResults: PageListDetailsSearchResults;
};

export function PageListAToZ({
  pageListAToZ,
  displayOptions,
  facets,
  resetAllFiltersUrl,
  searchResults,
}: PageListAToZProps) {
  const { t } = useSonTranslation('PageListDetails');

  const titleLabel = t('pageList.label.title');
  const descriptionLabel = t('pageList.label.shortDescriptionLabel');
  const attachmentLabel = t('pageList.label.attachmentsLabel');
  const caption = t('pageList.caption');

  const showColumn = (columnIdentifier: string) => !displayOptions?.hideColumns.includes(columnIdentifier);

  const renderTableHead = () => {
    const tableCellClass = 'tables__tableheader--dark-border';

    return (
      <TableHead>
        <TableRow>
          <TableCell className={tableCellClass} modifier={TableModifier.LWIDTH}>
            {titleLabel}
          </TableCell>
          <TableCell className={tableCellClass}>{descriptionLabel}</TableCell>
          {showColumn(ColumnIdentifier.attachment) && (
            <TableCell className={tableCellClass}>{attachmentLabel}</TableCell>
          )}
        </TableRow>
      </TableHead>
    );
  };

  const renderTableBody = () => {
    const tableCellClass = 'tables__tabledata--dark-border';

    const cellProperties = { className: tableCellClass };

    return (
      pageListAToZ &&
      Object.keys(pageListAToZ).map(key => (
        <TableBody key={`body-${key}`}>
          <TableRow>
            <TableCell
              colSpan={showColumn(ColumnIdentifier.attachment) ? 3 : 2}
              className={`${tableCellClass} tables__tableheader--padding-decreased tables__tableheader--color-dark`}
            >
              <AToZToNavButton id={key} hint={t('pageList.characterHint')} />
            </TableCell>
          </TableRow>
          {pageListAToZ &&
            pageListAToZ[key].map(row => (
              <TableRow key={row.pageLink.url}>
                <TableCellPageLink
                  label={titleLabel}
                  cellProperties={cellProperties}
                  pageLink={row.pageLink}
                  pagePackState={row.packState}
                  archiveDate={row.archiveDate}
                  integrated={row.integrated}
                  categories={row.categories}
                />
                <TableCellDescription
                  label={descriptionLabel}
                  cellProperties={cellProperties}
                  externalLink={row.externalLink}
                  description={getDisplayedTextByConfig(row, displayOptions.displayedInformationType)}
                />
                {showColumn(ColumnIdentifier.attachment) && (
                  <TableCell {...cellProperties} dataHead={attachmentLabel}>
                    <ContentAttachmentList attachments={row.attachments} />
                  </TableCell>
                )}
              </TableRow>
            ))}
        </TableBody>
      ))
    );
  };

  if (isNullOrUndefined(pageListAToZ)) {
    return null;
  }

  return (
    <>
      {searchResults?.searchForm ? (
        <SearchFormBase
          idPrefix="content"
          modifier={SearchFormModifier.PAGE_LIST}
          maxLength={searchResults.maxLength}
        />
      ) : null}
      <AToZContainer
        facets={facets}
        characters={pageListAToZ}
        invisibleHeadline={t('pageList.headline')}
        characterHint={t('pageList.characterHint')}
        resetAllFiltersUrl={resetAllFiltersUrl}
      >
        <Table caption={caption}>
          {renderTableHead()}
          {renderTableBody()}
        </Table>
      </AToZContainer>
    </>
  );
}
